import type { ProfileModuleLike } from './types';
import { extractYouTubeVideoId } from './youtube';

/**
 * Returns true if the module is enabled and has content to display.
 * Used to hide empty modules on public profiles and previews.
 */
export function hasModuleContent(module: ProfileModuleLike): boolean {
  if (!module.enabled) return false;
  const config = (module.config || {}) as Record<string, unknown>;
  const type = module.type;

  switch (type) {
    case 'biography': {
      const content = (config.content as string)?.trim();
      return !!content;
    }
    case 'contact_info': {
      const items =
        (config.items as Array<{ value?: string; visible?: boolean }>) ?? [];
      return items.some(
        (entry) => entry.visible !== false && (entry.value ?? '').trim(),
      );
    }
    case 'socials': {
      const links =
        (config.links as Array<{ url?: string; visible?: boolean }>) || [];
      return links.some((l) => l.visible !== false && (l.url ?? '').trim());
    }
    case 'links': {
      const linkItems =
        (config.items as Array<{ url?: string; visible?: boolean }>) || [];
      return linkItems.some((i) => i.visible !== false && (i.url ?? '').trim());
    }
    case 'video': {
      return extractYouTubeVideoId((config.url as string) || '') != null;
    }
    case 'images': {
      const images =
        (config.images as Array<{
          file?: { path?: string };
          visible?: boolean;
        }>) || [];
      return images.some((img) => img.visible !== false && img.file?.path);
    }
    case 'files': {
      const files =
        (config.files as Array<{
          file?: { path?: string };
          visible?: boolean;
        }>) || [];
      return files.some((f) => f.visible !== false && f.file?.path);
    }
    case 'google_maps': {
      const locationId = (config.locationId as string | undefined)?.trim();
      if (locationId) return true;
      const address = (config.address as string)?.trim();
      const lat = config.latitude as number | undefined;
      const lng = config.longitude as number | undefined;
      return !!(address || (lat != null && lng != null));
    }
    case 'team_members': {
      const members =
        (config.members as Array<{ visible?: boolean }> | undefined) ?? [];
      return members.some((member) => member.visible !== false);
    }
    default:
      return false;
  }
}
